import type { TelemetryEvent } from './schema';

type Listener = (e: TelemetryEvent) => void;

const buffer: TelemetryEvent[] = [];
const listeners = new Set<Listener>();
const MAX = 5000;

// Runtime guard: every event must have a non-empty string `type` and numeric `ts`.
// Drops malformed events so schema drift cannot poison the in-memory buffer.
function isValid(e: unknown): e is TelemetryEvent {
  if (!e || typeof e !== 'object') return false;
  const ev = e as Record<string, unknown>;
  return typeof ev.type === 'string'
    && ev.type.length > 0
    && typeof ev.ts === 'number'
    && Number.isFinite(ev.ts);
}

export const telemetry = {
  emit(e: TelemetryEvent) {
    if (!isValid(e)) {
      return;
    }
    buffer.push(e);
    if (buffer.length > MAX) buffer.shift();
    listeners.forEach((l) => l(e));
  },
  subscribe(l: Listener) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  dump(): TelemetryEvent[] {
    return buffer.slice();
  },
  clear() {
    buffer.length = 0;
  },
};

declare global {
  interface Window {
    __telemetry: typeof telemetry;
  }
}

export function installTelemetry() {
  window.__telemetry = telemetry;
  telemetry.emit({ type: 'app.boot.start', ts: performance.now() });
  window.addEventListener('load', () => {
    telemetry.emit({ type: 'app.boot.ready', ts: performance.now(), durationMs: performance.now() });
  });
}
